import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChevronRight } from 'lucide-react';
import socratesBust from '@/assets/socrates-philosopher-bust.png';
import { useLeagueConfig } from '@/hooks/useLeagueConfig';

const TOTAL_SCREENS = 3;

export function WelcomeModal() {
  const { currentMembership, refreshMemberships } = useAuth();
  const { scoringRules, config: leagueConfig } = useLeagueConfig();
  const [dismissing, setDismissing] = useState(false);
  const [currentScreen, setCurrentScreen] = useState(0);
  const [countdown, setCountdown] = useState(5);

  const show = !!currentMembership && !currentMembership.welcomed_at;
  const isLastScreen = currentScreen === TOTAL_SCREENS - 1;
  const ready = !isLastScreen || countdown <= 0;

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setCurrentScreen(0);
      setCountdown(5);
    }
  }, [show]);

  // Start countdown only on the last screen
  useEffect(() => {
    if (!show || !isLastScreen) return;

    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [show, isLastScreen]);

  const handleNext = useCallback(async () => {
    if (!isLastScreen) {
      setCurrentScreen((prev) => prev + 1);
      return;
    }

    // Last screen — dismiss
    if (!currentMembership || !ready) return;

    setDismissing(true);
    try {
      await supabase.rpc('mark_membership_welcomed', {
        p_membership_id: currentMembership.id,
      });
      await refreshMemberships();
      // On success, welcomed_at is set and `show` becomes false,
      // unmounting this component — no need to reset state.
    } catch (err) {
      console.error('Failed to dismiss welcome modal:', err);
      setDismissing(false);
    }
  }, [currentMembership, ready, refreshMemberships, isLastScreen]);

  const formatPoints = (n: number) => (n > 0 ? `+${n}` : `${n}`);
  const scoringDisplay = [
    { result: 'Win', points: formatPoints(scoringRules.win), accent: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/40' },
    { result: 'Draw', points: formatPoints(scoringRules.draw), accent: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/40' },
    { result: 'Loss', points: `${scoringRules.loss}`, accent: 'text-zinc-400 dark:text-zinc-500', bg: 'bg-zinc-50 dark:bg-zinc-800/40' },
    { result: 'Win by 3+', points: formatPoints(scoringRules.win_by_3_plus_bonus), accent: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/40' },
    { result: 'Red Card', points: `${scoringRules.red_card}`, accent: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/40' },
  ];

  return (
    <Dialog open={show} onOpenChange={(open) => { if (!open && ready && isLastScreen) handleNext(); }}>
      <DialogContent
        className="sm:max-w-md p-0 gap-0 overflow-hidden border-zinc-200 dark:border-zinc-700"
        onPointerDownOutside={(e) => { if (!ready || !isLastScreen) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!ready || !isLastScreen) e.preventDefault(); }}
      >
        {/* Screen 1: Football is about results */}
        {currentScreen === 0 && (
          <>
            <div className="relative bg-zinc-900 dark:bg-zinc-950 px-6 pt-8 pb-6 text-center overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_60%,rgba(255,255,255,0.06),transparent_70%)]" />
              <div className="relative">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-zinc-700/50 ring-2 ring-zinc-600/50 overflow-hidden">
                  <img
                    src={socratesBust}
                    alt="Socrates"
                    className="w-full h-full object-cover"
                  />
                </div>
                <DialogTitle className="font-caveat text-3xl font-bold text-white tracking-wide">
                  Football is about results
                </DialogTitle>
              </div>
            </div>

            <div className="px-6 pt-5 pb-6 space-y-4">
              <p className="text-lg font-medium text-zinc-800 dark:text-zinc-200 text-center leading-relaxed">
                Choose your league. Pick one team each round. Compete all season.
              </p>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 text-center leading-relaxed">
                Welcome to Socrates — the prediction league that rewards smart picks and strategic thinking.
              </p>
            </div>
          </>
        )}

        {/* Screen 2: Scoring */}
        {currentScreen === 1 && (
          <>
            <div className="relative bg-zinc-900 dark:bg-zinc-950 px-6 pt-8 pb-6 text-center overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_60%,rgba(255,255,255,0.06),transparent_70%)]" />
              <div className="relative">
                <DialogTitle className="font-caveat text-3xl font-bold text-white tracking-wide">
                  Scoring
                </DialogTitle>
              </div>
            </div>

            <div className="px-6 pt-5 pb-6 space-y-4">
              <div className="grid grid-cols-5 gap-1.5">
                {scoringDisplay.map((rule) => (
                  <div
                    key={rule.result}
                    className={`${rule.bg} rounded-lg py-2 px-1 text-center`}
                  >
                    <div className={`text-base font-bold font-mono ${rule.accent}`}>
                      {rule.points}
                    </div>
                    <div className="text-[10px] font-medium text-zinc-500 dark:text-zinc-400 mt-0.5 leading-tight">
                      {rule.result}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-zinc-50 dark:bg-zinc-800/40 rounded-lg p-3">
                <p className="text-sm text-zinc-700 dark:text-zinc-300 font-medium mb-1">
                  Double Downs
                </p>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                  You get <span className="font-semibold text-zinc-800 dark:text-zinc-200">{leagueConfig.tdd_limit} Double Downs</span> per season.
                  Use them to double your points (positive or negative) for a matchday.
                </p>
              </div>
            </div>
          </>
        )}

        {/* Screen 3: Make Your Pick */}
        {currentScreen === 2 && (
          <>
            <div className="relative bg-zinc-900 dark:bg-zinc-950 px-6 pt-8 pb-6 text-center overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_60%,rgba(255,255,255,0.06),transparent_70%)]" />
              <div className="relative">
                <DialogTitle className="font-caveat text-3xl font-bold text-white tracking-wide">
                  Make Your Pick
                </DialogTitle>
              </div>
            </div>

            <div className="px-6 pt-5 pb-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</div>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                    <span className="font-medium text-zinc-800 dark:text-zinc-200">One pick per matchday.</span> Choose a team you think will win.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</div>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                    You can pick each team <span className="font-medium text-zinc-800 dark:text-zinc-200">up to {leagueConfig.team_pick_limit} times per season</span>, so plan strategically.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</div>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                    <span className="font-medium text-zinc-800 dark:text-zinc-200">Lock in before the deadline</span> — you can change your pick until it closes.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Navigation: dots + next button */}
        <div className="px-6 pb-6 flex items-center justify-between">
          {/* Dots */}
          <div className="flex items-center gap-2">
            {Array.from({ length: TOTAL_SCREENS }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  i === currentScreen
                    ? 'bg-zinc-900 dark:bg-zinc-100'
                    : 'bg-zinc-300 dark:bg-zinc-600'
                }`}
              />
            ))}
          </div>

          {/* Next button + countdown */}
          <div className="flex items-center gap-3">
            {isLastScreen && !ready && (
              <span className="text-xs font-mono tabular-nums text-zinc-400 dark:text-zinc-500">
                {countdown}
              </span>
            )}
            <button
              onClick={handleNext}
              disabled={(!ready && isLastScreen) || dismissing}
              className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150
                ${ready || !isLastScreen
                  ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 active:scale-[0.97]'
                  : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-400 dark:text-zinc-500 cursor-not-allowed'
                }`}
            >
              {dismissing ? 'Loading...' : isLastScreen ? "Let's go" : 'Next'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
